import React, { useEffect, useState } from 'react';
import { Box, Typography, Container, Grid, useTheme, useMediaQuery, Button, Checkbox, Chip, IconButton, Tooltip } from '@mui/material';
import { Icon } from '@iconify/react';
import TopBar from '../components/TopBar';
import Sidebar from '../components/Sidebar';
import DataTable, { Column, RowAction, FilterChip } from '../components/DataTable';
import EPRCViewerDrawer from '../components/EPRCViewerDrawer';
import BulkEPRCModal from '../components/BulkEPRCModal';
import { useToast } from '../components/ui/ToastProvider';
import { VALID_MTOS, type ValidMTO } from '../utils/constants';
interface EPRCGenerationProps {
  onLogout: () => void;
}
interface Transaction {
  id: string;
  mtoRef: string;
  bankRef: string;
  mtoName: string;
  remitterName: string;
  beneficiaryName: string;
  beneficiaryIBAN: string;
  amountFCY: string;
  currencyFCY: string;
  amountPKR: string;
  purposeCode: string;
  purposeText: string;
  payoutType: 'FT' | 'RAAST' | 'ONELINK' | 'RTGS';
  corridor: string;
  lifecycleStatus: 'SUCCESS';
  currentState: string;
  createdAt: string;
  updatedAt: string;
  ePRCNo?: string;
  ePRCDate?: string;
  stateHistory: any[];
  railRefs: {
    RaastMsgId?: string;
    RRN?: string;
    STAN?: string;
    UETR?: string;
  };
}
// Mock completed transactions data
const MOCK_COMPLETED_TRANSACTIONS: Transaction[] = Array(50).fill(null).map((_, index) => {
  const corridors = ['GH-UK', 'NG-US', 'SN-FR', 'KE-DE'];
  const corridor = corridors[index % 4];
  const currencyMap: Record<string, string> = {
    'GH-UK': 'GBP',
    'NG-US': 'USD',
    'SN-FR': 'EUR',
    'KE-DE': 'EUR'
  };
  const fxMap: Record<string, number> = {
    'GH-UK': 15,
    'NG-US': 1500,
    'SN-FR': 655,
    'KE-DE': 155
  };
  const ibanMap: Record<string, string> = {
    'GH-UK': `GB29NWBK601613319268${String(index).padStart(2, '0')}`,
    'NG-US': `US02100002112345${String(6789 + index).padStart(4, '0')}`,
    'SN-FR': `FR76300060000112345678901${String(index).padStart(2, '0')}`,
    'KE-DE': `DE8937040044053201${String(3000 + index).padStart(4, '0')}`
  };
  const fcy = 500 + index * 10;
  return {
    id: `TXN-${1000 + index}`,
    mtoRef: `MTO-REF-${2000 + index}`,
    bankRef: `BNK-${3000 + index}`,
    mtoName: VALID_MTOS[index % VALID_MTOS.length],
    remitterName: ['Kwame Asante', 'Adekunle Okoro', 'Fatoumata Ndiaye', 'Grace Kipchoge', 'Amara Jalloh'][index % 5],
    beneficiaryName: ['Samuel Mensah', 'David Johnson', 'Marie Dupont', 'Klaus Schmidt', 'Michael Brown'][index % 5],
    beneficiaryIBAN: ibanMap[corridor],
    amountFCY: fcy.toFixed(2),
    currencyFCY: currencyMap[corridor],
    amountPKR: (fcy * fxMap[corridor]).toFixed(2),
    purposeCode: ['FAM', 'SAL', 'EDU', 'MED'][index % 4],
    purposeText: ['Personal Remittance', 'Business Payment', 'Education', 'Medical'][index % 4],
    payoutType: (['FT', 'RAAST', 'ONELINK', 'RTGS'] as const)[index % 4],
    corridor,
    lifecycleStatus: 'SUCCESS' as const,
    currentState: 'SUCCESS',
    createdAt: new Date(Date.now() - index * 3600000).toISOString(),
    updatedAt: new Date(Date.now() - index * 1800000).toISOString(),
    ePRCNo: index % 3 === 0 ? `EPRC-${4000 + index}` : undefined,
    ePRCDate: index % 3 === 0 ? new Date(Date.now() - index * 900000).toISOString() : undefined,
    stateHistory: [],
    railRefs: {
      RaastMsgId: index % 4 === 1 ? `RAAST-${index}` : undefined,
      RRN: index % 4 === 0 ? `RRN${String(index).padStart(12, '0')}` : undefined,
      STAN: index % 4 === 0 ? String(index).padStart(6, '0') : undefined,
      UETR: index % 4 === 3 ? `UETR-${index}` : undefined
    }
  };
});
export default function EPRCGeneration({
  onLogout
}: EPRCGenerationProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [transactions, setTransactions] = useState<Transaction[]>(MOCK_COMPLETED_TRANSACTIONS);
  const [selectedTransactions, setSelectedTransactions] = useState<Set<string>>(new Set());
  const [eprcDrawerOpen, setEprcDrawerOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
  const [bulkEPRCModalOpen, setBulkEPRCModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const {
    showToast
  } = useToast();
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };
  const handleSelectTransaction = (transactionId: string) => {
    const newSelected = new Set(selectedTransactions);
    if (newSelected.has(transactionId)) {
      newSelected.delete(transactionId);
    } else {
      newSelected.add(transactionId);
    }
    setSelectedTransactions(newSelected);
  };
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedTransactions(new Set(transactions.map(t => t.id)));
    } else {
      setSelectedTransactions(new Set());
    }
  };
  const handleViewEPRC = (transaction: Transaction) => {
    setSelectedTransaction(transaction);
    setEprcDrawerOpen(true);
  };
  const handleDownloadEPRC = (transaction: Transaction) => {
    showToast(`Downloading e-PRC for ${transaction.ePRCNo}`, 'success', 'Download Started');
  };
  const handleViewBulkEPRC = () => {
    if (selectedTransactions.size === 0) {
      showToast('Please select at least one transaction', 'warning');
      return;
    }
    setBulkEPRCModalOpen(true);
  };
  const handleClearSelection = () => {
    setSelectedTransactions(new Set());
  };
  // Define columns
  const columns: Column<Transaction>[] = [{
    id: 'select',
    label: <Checkbox checked={selectedTransactions.size === transactions.length && transactions.length > 0} indeterminate={selectedTransactions.size > 0 && selectedTransactions.size < transactions.length} onChange={e => handleSelectAll(e.target.checked)} sx={{
      color: '#4F46E5',
      '&.Mui-checked': {
        color: '#4F46E5'
      }
    }} data-id="element-4026" />,
    renderCell: row => <Checkbox checked={selectedTransactions.has(row.id)} onChange={() => handleSelectTransaction(row.id)} sx={{
      color: '#4F46E5',
      '&.Mui-checked': {
        color: '#4F46E5'
      }
    }} data-id="element-4027" />
  }, {
    id: 'id',
    label: 'Transaction ID'
  }, {
    id: 'mtoRef',
    label: 'MTO Reference'
  }, {
    id: 'bankRef',
    label: 'Bank Reference'
  }, {
    id: 'mtoName',
    label: 'MTO',
    renderCell: row => <Chip label={row.mtoName} size="small" sx={{
      backgroundColor: 'rgba(14, 165, 233, 0.1)',
      color: '#0EA5E9',
      fontWeight: 600
    }} data-id="element-4028" />
  }, {
    id: 'remitterName',
    label: 'Remitter'
  }, {
    id: 'beneficiaryName',
    label: 'Beneficiary'
  }, {
    id: 'amountFCY',
    label: 'Amount (FCY)',
    renderCell: row => <Typography variant="body2" fontWeight={600} data-id="element-4029">
          {row.currencyFCY} {row.amountFCY}
        </Typography>
  }, {
    id: 'amountPKR',
    label: 'Local Amount',
    renderCell: row => <Typography variant="body2" fontWeight={600} data-id="element-4030">
          {row.amountPKR}
        </Typography>
  }, {
    id: 'payoutType',
    label: 'Payout Type',
    renderCell: row => <Chip label={row.payoutType} size="small" sx={{
      backgroundColor: row.payoutType === 'FT' ? 'rgba(14, 165, 233, 0.1)' : row.payoutType === 'RAAST' ? 'rgba(139, 92, 246, 0.1)' : row.payoutType === 'ONELINK' ? 'rgba(245, 158, 11, 0.1)' : 'rgba(16, 185, 129, 0.1)',
      color: row.payoutType === 'FT' ? '#0EA5E9' : row.payoutType === 'RAAST' ? '#8B5CF6' : row.payoutType === 'ONELINK' ? '#F59E0B' : '#10B981',
      fontWeight: 600
    }} data-id="element-4031" />
  }, {
    id: 'ePRCNo',
    label: 'e-PRC Number',
    renderCell: row => <Typography variant="body2" fontFamily="monospace" fontSize="0.8rem" data-id="element-4032">
          {row.ePRCNo}
        </Typography>
  }, {
    id: 'receipt',
    label: 'Receipt',
    renderCell: row => <Box sx={{
      display: 'flex',
      gap: 1
    }} data-id="element-4033">
          <Tooltip title="View Receipt" data-id="element-4034">
            <IconButton size="small" onClick={e => {
          e.stopPropagation();
          handleViewEPRC(row);
        }} sx={{
          color: '#4F46E5',
          '&:hover': {
            backgroundColor: 'rgba(79, 70, 229, 0.08)'
          }
        }} data-id="element-4035">
              <Icon icon="lucide:file-text" width={18} height={18} data-id="element-4036" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Download Receipt" data-id="element-4037">
            <IconButton size="small" onClick={e => {
          e.stopPropagation();
          handleDownloadEPRC(row);
        }} sx={{
          color: '#10B981',
          '&:hover': {
            backgroundColor: 'rgba(16, 185, 129, 0.08)'
          }
        }} data-id="element-4038">
              <Icon icon="lucide:download" width={18} height={18} data-id="element-4039" />
            </IconButton>
          </Tooltip>
        </Box>
  }, {
    id: 'createdAt',
    label: 'Date',
    renderCell: row => new Date(row.createdAt).toLocaleDateString()
  }];
  // Row actions - removed since we have inline actions now
  const rowActions: RowAction<Transaction>[] = [];
  // Apply search filter
  const filteredTransactions = transactions.filter(txn => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return txn.id.toLowerCase().includes(query) || txn.mtoRef.toLowerCase().includes(query) || txn.bankRef.toLowerCase().includes(query) || txn.mtoName.toLowerCase().includes(query) || txn.remitterName.toLowerCase().includes(query) || txn.beneficiaryName.toLowerCase().includes(query) || txn.ePRCNo?.toLowerCase().includes(query);
  });
  return <Box sx={{
    width: '100%',
    height: '100%',
    bgcolor: 'background.default'
  }} data-id="element-4040">
      <TopBar onMenuClick={handleDrawerToggle} onLogout={onLogout} data-id="element-4041" />
      <Sidebar open={mobileOpen} onClose={handleDrawerToggle} activePage="eprc-generation" data-id="element-4042" />
      <Box component="main" sx={{
      flexGrow: 1,
      py: 2,
      px: 1,
      width: {
        xs: '100%',
        sm: `calc(100% - ${240}px)`
      },
      ml: {
        xs: 0,
        sm: `${240}px`
      },
      mt: 8
    }} data-id="element-4043">
        <Container maxWidth="xl" data-id="element-4044">
          {/* Header */}
          <Grid container spacing={2} alignItems="center" sx={{
          mb: 3
        }} data-id="element-4045">
            <Grid item xs={12} md={6} data-id="element-4046">
              <Box sx={{
              display: 'flex',
              alignItems: 'center'
            }} data-id="element-4047">
                <Icon icon="lucide:file-text" width={24} height={24} color="#0EA5E9" data-id="element-4048" />
                <Typography variant="h5" component="h1" sx={{
                ml: 1.5,
                fontWeight: 600
              }} data-id="element-4049">
                  e-PRC Generation
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={6} data-id="element-4050">
              <Box sx={{
              display: 'flex',
              gap: 2,
              flexWrap: 'wrap',
              justifyContent: {
                xs: 'flex-start',
                md: 'flex-end'
              }
            }} data-id="element-4051">
                {selectedTransactions.size > 0 && <>
                    <Button variant="outlined" startIcon={<Icon icon="lucide:x" width={18} height={18} data-id="element-4053" />} onClick={handleClearSelection} data-id="element-4052">
                      Clear ({selectedTransactions.size})
                    </Button>
                    <Button variant="contained" startIcon={<Icon icon="lucide:file-stack" width={18} height={18} data-id="element-4055" />} onClick={handleViewBulkEPRC} data-id="element-4054">
                      View Bulk e-PRC ({selectedTransactions.size})
                    </Button>
                  </>}
              </Box>
            </Grid>
          </Grid>

          {/* Transactions Table */}
          <DataTable title="Completed Transactions" data={filteredTransactions} columns={columns} rowActions={rowActions} keyField="id" onSearchChange={setSearchQuery} searchValue={searchQuery} searchPlaceholder="Search by transaction ID, MTO ref, bank ref, e-PRC number..." emptyStateMessage="No completed transactions found" initialVisibleColumns={['select', 'id', 'mtoRef', 'bankRef', 'mtoName', 'remitterName', 'beneficiaryName', 'amountFCY', 'amountPKR', 'payoutType', 'ePRCNo', 'receipt', 'createdAt']} data-id="element-4056" />

          {/* e-PRC Viewer Drawer */}
          {selectedTransaction && <EPRCViewerDrawer open={eprcDrawerOpen} onClose={() => {
          setEprcDrawerOpen(false);
          setSelectedTransaction(null);
        }} transaction={selectedTransaction} data-id="element-4057" />}

          {/* Bulk e-PRC Modal */}
          <BulkEPRCModal open={bulkEPRCModalOpen} onClose={() => setBulkEPRCModalOpen(false)} transactions={transactions.filter(t => selectedTransactions.has(t.id))} data-id="element-4058" />
        </Container>
      </Box>
    </Box>;
}